/**
 * Cookie consent management library
 * Stores preferences in localStorage (not a cookie itself — no consent needed for localStorage of preferences)
 */

export interface CookiePreferences {
  necessary: true; // Always true, cannot be changed
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

const STORAGE_KEY = 'exora_cookie_consent';

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
  timestamp: '',
};

/**
 * Check if the user has already made a cookie consent decision
 */
export function hasConsentDecision(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return !!parsed.timestamp;
  } catch {
    return false;
  }
}

/**
 * Get current cookie preferences
 */
export function getPreferences(): CookiePreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_PREFERENCES;
    return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Save cookie preferences
 */
export function savePreferences(prefs: Partial<CookiePreferences>): CookiePreferences {
  const updated: CookiePreferences = {
    ...DEFAULT_PREFERENCES,
    ...prefs,
    necessary: true, // Always true
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  
  // Dispatch custom event so other parts of the app (e.g., Sentry) can react
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: updated }));
  
  return updated;
}

/**
 * Accept all cookies
 */
export function acceptAll(): CookiePreferences {
  return savePreferences({
    preferences: true,
    analytics: true,
    marketing: true,
  });
}

/**
 * Reject all non-essential cookies
 */
export function rejectAll(): CookiePreferences {
  return savePreferences({
    preferences: false,
    analytics: false,
    marketing: false,
  });
}

/**
 * Check if analytics cookies are consented
 */
export function hasAnalyticsConsent(): boolean {
  return getPreferences().analytics;
}

/**
 * Check if marketing cookies are consented
 */
export function hasMarketingConsent(): boolean {
  return getPreferences().marketing;
}

/**
 * Reset consent (for testing or re-prompting)
 */
export function resetConsent(): void {
  localStorage.removeItem(STORAGE_KEY);
}
